import React from 'react';
import Icon from '../Icon';
import { Menu, MenuItem } from '../Menu';
import { signedMinor, CATEGORY_UNSET_LABEL } from '../../domain/expenses';
import { entryRowActions } from '../../domain/expenseForms';
import { isMileageExpenseId } from '../../domain/mileage';
import { useMoneyFormat, useDateFormat } from '../../context/LocaleContext';
import s from './ExpenseTable.module.css';

// The date pattern is the studio's: MMS's Region setting drives currency and
// date format from one choice (src/domain/locale.js), so a UK studio gets
// 12/09/2026 here and £ in the next column. No Date parsing — formatDateOnly
// substitutes into the string, so the value cannot slip a day west of
// Greenwich.
//
// `attachments` is a prop (#183), not read off the entry: a receipt is its own
// document in `expenseAttachments`, and this component draws a row of a list,
// so it is handed the answer as it is handed `entry`. The resolution happens
// once in `ExpensesPage` through `useExpenses().attachmentsFor`, which also
// draws a row's older inline receipts.
//
// Defaulted, so a caller that has not wired it renders a row with no paperclip
// rather than throwing; this component cannot tell "no receipts" from "not
// wired".
export default function ExpenseRow({
  entry, attachments = [], menuOpen, onMenuToggle, onMenuClose, onEdit, onDelete,
  onOpenAttachment,
}) {
  const formatMoney = useMoneyFormat();
  const shortDate = useDateFormat();
  const actions = entryRowActions(Boolean(entry.instanceOf));
  // An expense the Mileage Log made (#520) is the trip's to change: MMS's
  // Expenses list will not open one — no ⋮, and a click does nothing (#554).
  // Its own id, or a recurring instance's rule's for a series saved before
  // #554.
  const fromMileage = isMileageExpenseId(entry.instanceOf || entry.id);
  const amount = formatMoney(signedMinor(entry));
  // Filtered to the ones with an object behind them: a card with no path has
  // nothing to open, and rendering it would be a dead link.
  const receipts = attachments.filter((a) => a?.path);

  return (
    <tr>
      <td>{shortDate(entry.occurredOn) || '–'}</td>
      <td>
        {entry.type === 'expense'
          ? <span className={`${s.chip} ${s.chipExpense}`}>{amount}</span>
          : '–'}
      </td>
      <td>
        {entry.type === 'revenue'
          ? <span className={`${s.chip} ${s.chipRevenue}`}>{amount}</span>
          : '–'}
      </td>
      <td>{entry.category || CATEGORY_UNSET_LABEL}</td>
      <td>{entry.payee || '–'}</td>
      {/* One link per attachment, because an entry can hold several (#104):
          a row with more than one names each file. */}
      <td>
        {receipts.length ? (
          <div className={s.attachments}>
            {receipts.map((a) => (
              <button
                key={a.path}
                className={s.attachment}
                title={a.name}
                onClick={() => onOpenAttachment(a.path)}
              >
                <Icon name="paperclip" />{receipts.length > 1 ? a.name : 'View'}
              </button>
            ))}
          </div>
        ) : '–'}
      </td>
      <td>{entry.description || '–'}</td>
      <td className={s.rowMenuCell}>
        {fromMileage ? null : (
          <Menu
            open={menuOpen}
            onClose={onMenuClose}
            trigger={(
              <button className={s.kebab} aria-label="Row actions" onClick={onMenuToggle}>⋮</button>
            )}
          >
            <MenuItem icon="pencil" onClick={() => onEdit(entry)}>
              {actions.edit}
            </MenuItem>
            {/* Deleting an instance of a recurring rule deletes the rule: the
                instances are derived, so there is no single month to remove. */}
            <MenuItem icon="trash" onClick={() => onDelete(entry)}>
              {actions.remove}
            </MenuItem>
          </Menu>
        )}
      </td>
    </tr>
  );
}
